import React from 'react';
import { BsFileEarmarkText } from 'react-icons/bs';
import {
  FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFileArchive,
  FaDownload, FaUser, FaClock,
} from 'react-icons/fa';
import { FiTrash2 } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import confirmAlert from 'components/common/confirmAlert';
import { formatFileSize } from './fileSize';

// ── file type config ──────────────────────────────────────────────────────────
const FILE_MAP = {
  pdf:  { Icon: FaFilePdf,     color: '#ef4444' },
  doc:  { Icon: FaFileWord,    color: '#3b82f6' },
  docx: { Icon: FaFileWord,    color: '#3b82f6' },
  xls:  { Icon: FaFileExcel,   color: '#22c55e' },
  xlsx: { Icon: FaFileExcel,   color: '#22c55e' },
  jpg:  { Icon: FaFileImage,   color: '#f97316' },
  jpeg: { Icon: FaFileImage,   color: '#f97316' },
  png:  { Icon: FaFileImage,   color: '#f97316' },
  gif:  { Icon: FaFileImage,   color: '#f97316' },
  svg:  { Icon: FaFileImage,   color: '#f97316' },
  zip:  { Icon: FaFileArchive, color: '#a855f7' },
  rar:  { Icon: FaFileArchive, color: '#a855f7' },
  '7z': { Icon: FaFileArchive, color: '#a855f7' },
};

// ── revision chip ─────────────────────────────────────────────────────────────
// Kept on one line (`whitespace-nowrap` + `flex-shrink-0`): inside a narrow
// table cell the label used to break between "New ·" and "REV#01".
const CHIP_BASE =
  'tw-inline-flex tw-items-center tw-gap-1 tw-flex-shrink-0 tw-whitespace-nowrap ' +
  'tw-rounded-sm tw-border tw-px-1.5 tw-py-[3px] tw-text-[10px] tw-font-bold ' +
  'tw-uppercase tw-tracking-wide tw-leading-none';

const REVISION_CHIPS = {
  new: {
    label: 'New',
    className: 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200',
  },
  replace: {
    label: 'Replaced',
    className: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
  },
};

const getExt = (filename) =>
  filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();

// ── DocumentAttachment ────────────────────────────────────────────────────────
const DocumentAttachment = ({
  id, name, size, url,
  created_by, created_date,
  allowView, allowDownload, allowRemove,
  handleRemoveAttachment,
  isTable,
  revision_action, revision_rev,
}) => {

  const handleDownload = async () => {
    try {
      const res  = await fetch(url);
      const blob = await res.blob();
      const a    = document.createElement('a');
      a.href     = window.URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, '_blank');
    }
  };

  const handleConfirmRemove = () => {
    confirmAlert({
      title:               'Confirmation',
      message:             'Are you sure you want to remove this attachment? This action cannot be undone.',
      closeOnEscape:       true,
      closeOnClickOutside: false,
      buttons: [
        // Do NOT swallow errors here — a silent `catch {}` previously hid the real
        // failure, so a broken remove looked like "nothing happened / no API call".
        // Surface it (toast + console) so the actual cause is visible.
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await handleRemoveAttachment(id);
            } catch (error) {
              console.error('Remove attachment failed:', error);
              toast.error(error?.message || 'Failed to remove attachment.');
            }
          },
        },
        { label: 'No',  onClick: () => {} },
      ],
    });
  };

  const ext = getExt(name);
  const { Icon, color } = FILE_MAP[ext] ?? { Icon: BsFileEarmarkText, color: '#64748b' };
  const revisionChip = REVISION_CHIPS[revision_action];

  return (
    // tw-shrink-0: a row must keep its natural height. Inside a scrolling flex
    // column (the Attachments panel) the default flex-shrink squashes every row
    // to fit the container instead of letting it scroll, and tw-overflow-hidden
    // then clips the name and meta lines into each other.
    <div
      className="tw-group tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-shrink-0 tw-overflow-hidden tw-items-center tw-gap-2.5 tw-px-2.5 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded hover:tw-border-slate-300 hover:tw-bg-slate-50/50 tw-transition-colors tw-text-[14px]"
    >
      {/* File type icon */}
      <span
        className="tw-flex tw-items-center tw-justify-center tw-rounded-md tw-flex-shrink-0"
        style={{ width: 32, height: 32, backgroundColor: `${color}18` }}
      >
        <Icon size={16} style={{ color }} />
      </span>

      {/* Name + meta */}
      <div className="tw-flex-1 tw-min-w-0">
        {/* Name row — the revision chip rides with the name so it can never be
            squeezed apart mid-label by the meta row wrapping. */}
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
          {allowView ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-truncate tw-leading-tight tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline tw-text-[13px]"
            >
              {name}
            </a>
          ) : (
            <span
              className="tw-truncate tw-leading-tight tw-font-semibold tw-text-slate-700 tw-text-[13px]"
            >
              {name}
            </span>
          )}
          {revisionChip && (
            <span className={`${CHIP_BASE} ${revisionChip.className}`}>
              {revisionChip.label}
              {revision_rev != null && (
                <span className="tw-opacity-60">REV#{revision_rev}</span>
              )}
            </span>
          )}
        </div>

        {/* Compact meta row */}
        <div
          className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-mt-1 tw-text-slate-400 tw-leading-none tw-text-[12px]"
        >
          <span className="tw-font-medium tw-text-slate-500 tw-whitespace-nowrap">{formatFileSize(size)}</span>
          <span className="tw-text-slate-300">·</span>
          <span className="tw-uppercase tw-tracking-wide tw-text-[11px] tw-whitespace-nowrap">{ext || 'FILE'}</span>

          {isTable && created_by && (
            <>
              <span className="tw-text-slate-300">·</span>
              <span className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
                <FaUser size={9} />
                <span className="tw-font-medium tw-text-slate-500">{created_by}</span>
              </span>
            </>
          )}

          {isTable && created_date && (
            <>
              <span className="tw-text-slate-300">·</span>
              <span className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
                <FaClock size={9} />
                <span>{created_date}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Action buttons — always visible (each is already permission-gated below) */}
      <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0">
        {allowDownload && (
          <button
            type="button"
            onClick={handleDownload}
            title="Download"
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-sm tw-text-blue-400 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors"
          >
            <FaDownload size={12} />
          </button>
        )}
        {allowRemove && (
          <button
            type="button"
            onClick={handleConfirmRemove}
            title="Remove"
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-sm tw-text-red-400 hover:tw-text-red-600 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors"
          >
            <FiTrash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

DocumentAttachment.propTypes = {
  id:                     PropTypes.string.isRequired,
  name:                   PropTypes.string.isRequired,
  size:                   PropTypes.number.isRequired,
  url:                    PropTypes.string.isRequired,
  created_by:             PropTypes.string.isRequired,
  created_date:           PropTypes.string.isRequired,
  allowView:              PropTypes.bool.isRequired,
  allowDownload:          PropTypes.bool.isRequired,
  allowRemove:            PropTypes.bool.isRequired,
  handleRemoveAttachment: PropTypes.func.isRequired,
  isTable:                PropTypes.bool,
  revision_action:        PropTypes.oneOf(['new', 'replace']),
  revision_rev:           PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DocumentAttachment;
